import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { CONTENT_TYPES } from './constants';

function normaliseAlias(value) {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, '_');
}

function validate(form) {
    const errors = {};
    if (!form.name.trim()) errors.name = 'El nombre es requerido';
    if (!normaliseAlias(form.alias)) errors.alias = 'El alias es requerido';
    if (form.content.length === 0) errors.content = 'Selecciona al menos un tipo';
    return errors;
}

export function GeneralChannelForm({ initial, isNew, isComplete = false, onSubmit, saving, onCancel }) {
    const [form, setForm] = useState({ name: '', alias: '', active: false, content: [] });
    const [touched, setTouched] = useState({});
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        const base = initial || {};
        setForm({
            name: base.name || '',
            alias: base.alias || '',
            // Un canal incompleto no puede estar activo.
            active: isComplete ? !!base.active : false,
            content: base.content || [],
        });
        setTouched({});
        setSubmitted(false);
    }, [initial, isNew, isComplete]);

    const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
    const markTouched = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

    const errors = validate(form);
    const isValid = Object.keys(errors).length === 0;
    const showError = (field) => ((touched[field] || submitted) ? errors[field] : '');

    const preventEnter = (e) => {
        if (e.key === 'Enter') e.preventDefault();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
        if (!isValid) return;
        onSubmit({
            name: form.name.trim(),
            alias: normaliseAlias(form.alias),
            active: isComplete ? form.active : false,
            content: form.content,
        });
    };

    return (
        <form onSubmit={handleSubmit} autoComplete="off" noValidate>
            <div className="row g-3">
                <div className="col-md-6">
                    <label className="form-label text-white-50">Nombre *</label>
                    <input
                        type="text"
                        className={`form-control bg-dark text-white border-secondary py-2${showError('name') ? ' is-invalid' : ''}`}
                        value={form.name}
                        onChange={(e) => set('name', e.target.value)}
                        onBlur={() => markTouched('name')}
                    />
                    {showError('name') && <small className="text-danger d-block mt-1">{showError('name')}</small>}
                </div>
                <div className="col-md-6">
                    <label className="form-label text-white-50">Alias *</label>
                    <input
                        type="text"
                        className={`form-control bg-dark text-white border-secondary py-2${showError('alias') ? ' is-invalid' : ''}`}
                        value={form.alias}
                        onChange={(e) => set('alias', e.target.value.toLowerCase())}
                        onBlur={() => markTouched('alias')}
                        placeholder="es. amazon, bookmate, ibooks"
                    />
                    {showError('alias')
                        ? <small className="text-danger d-block mt-1">{showError('alias')}</small>
                        : <small className="text-white-50">Identificador interno único. Se normaliza a minúsculas.</small>}
                </div>

                <div className="col-md-6">
                    <label className="form-label text-white-50 d-block">Tipo de contenido *</label>
                    <div className="d-flex gap-3 flex-wrap" onKeyDown={preventEnter}>
                        {CONTENT_TYPES.map((c) => (
                            <div key={c.value} className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`content-${c.value}`}
                                    checked={form.content.includes(c.value)}
                                    onChange={(e) => {
                                        markTouched('content');
                                        const next = e.target.checked
                                            ? [...form.content, c.value]
                                            : form.content.filter((x) => x !== c.value);
                                        set('content', next);
                                    }}
                                />
                                <label className="form-check-label text-white" htmlFor={`content-${c.value}`}>{c.label}</label>
                            </div>
                        ))}
                    </div>
                    {showError('content') && <small className="text-danger d-block mt-1">{showError('content')}</small>}
                </div>

                <div className="col-md-6">
                    <label className="form-label text-white-50 d-block">Estado</label>
                    <div className="form-check form-switch">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            role="switch"
                            id="active-channel"
                            checked={form.active}
                            onChange={(e) => set('active', e.target.checked)}
                            disabled={!isComplete}
                            title={!isComplete ? 'Completa la configuración del canal para poder activarlo' : undefined}
                        />
                        <label className="form-check-label text-white" htmlFor="active-channel">
                            {form.active ? 'Activo' : 'Inactivo'}
                        </label>
                    </div>
                    {!isComplete && (
                        <small className="text-white-50 d-block mt-1">
                            Completa la configuración para poder activar el canal.
                        </small>
                    )}
                </div>
            </div>

            <div className="d-flex justify-content-end align-items-center mt-4 pt-3 border-top border-secondary border-opacity-25">
                {!isValid && <small className="text-white-50 me-auto">Completa los campos requeridos</small>}
                <button type="button" className="btn btn-outline-secondary px-4 me-2" onClick={onCancel} disabled={saving}>Cancelar</button>
                <button
                    type="submit"
                    className="btn btn-success px-4"
                    disabled={saving || !isValid}
                    title={!isValid ? 'Completa los campos requeridos' : undefined}
                >
                    {saving ? <FontAwesomeIcon icon={faSpinner} spin className="me-1" /> : null}
                    {isNew ? 'Crear canal' : 'Guardar'}
                </button>
            </div>
        </form>
    );
}

export default GeneralChannelForm;